import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import PackageCard from '../components/PackageCard';
import Reveal from '../components/Reveal';
import Loader, { ErrorState } from '../components/Loader';
import useAsync from '../hooks/useAsync';
import { getContent } from '../lib/content';
import { stagger, viewportOnce } from '../theme/theme';

export default function Packages() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const active = params.get('category') || 'all';

  const { data, loading, error, reload } = useAsync(getContent, []);
  const categories = data?.categories || [];

  const filtered = useMemo(() => {
    return (data?.packages || []).filter((p) => {
      const byCat = active === 'all' || p.categoryId === active;
      const byQuery =
        !query ||
        p.title.toLowerCase().includes(query.toLowerCase()) ||
        p.category.toLowerCase().includes(query.toLowerCase());
      return byCat && byQuery;
    });
  }, [data, active, query]);

  const setCat = (id) => {
    if (id === 'all') setParams({});
    else setParams({ category: id });
  };

  return (
    <div className="section py-12">
      <Reveal className="mb-8 text-center">
        <h1 className="font-display text-4xl font-extrabold">Our <span className="text-gradient">Packages</span></h1>
        <p className="mt-3 text-ink-300">Browse, compare and pick the perfect package for your event.</p>
      </Reveal>

      {/* Search */}
      <div className="mx-auto mb-6 flex max-w-md items-center gap-2 glass px-4 py-2">
        <Search size={18} className="text-ink-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search packages…"
          className="w-full bg-transparent py-1 text-sm text-white outline-none placeholder:text-ink-400"
        />
      </div>

      {/* Category chips */}
      <div className="mb-10 flex flex-wrap justify-center gap-2">
        {[{ id: 'all', name: 'All' }, ...categories].map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            className={`rounded-pill px-4 py-2 text-sm font-medium transition ${
              active === c.id
                ? 'bg-brand-gradient text-white shadow-brand-glow'
                : 'border border-white/10 text-ink-300 hover:text-white'
            }`}
          >
            {c.icon ? `${c.icon} ` : ''}{c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader label="Loading packages…" />
      ) : error ? (
        <ErrorState message="Couldn't load packages." onRetry={reload} />
      ) : filtered.length === 0 ? (
        <p className="py-20 text-center text-ink-400">No packages found. Try another search.</p>
      ) : (
        <motion.div
          key={active + query}
          variants={stagger(0.07)}
          initial="hidden"
          animate="show"
          viewport={viewportOnce}
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {filtered.map((p) => (
            <PackageCard key={p.id} pkg={p} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
